import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send } from "lucide-react";
import SpideyLogo from "./SpideyLogo";

const INFO_LINKS = [
    { to: "/infos/mentions-legales", label: "Mentions légales" },
    { to: "/infos/cgv", label: "CGV" },
    { to: "/infos/livraison-retours", label: "Livraison & retours" },
    { to: "/infos/remboursement", label: "Politique de remboursement" },
    { to: "/infos/contact", label: "Contact" },
];

const TikTokIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
);

export default function Footer() {
    const [email, setEmail] = useState("");

    const subscribe = (e) => {
        e.preventDefault();
        if (!email.includes("@")) {
            toast.error("Entre une adresse e-mail valide");
            return;
        }
        toast.success("Bienvenue dans le clan Spidey !");
        setEmail("");
    };

    return (
        <footer className="relative bg-spidey-panel/60 border-t border-zinc-900 overflow-hidden">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-8">
                <div className="grid md:grid-cols-[1.2fr_1fr_1fr] gap-12">
                    <div>
                        <Link to="/" aria-label="Retour à l'accueil" data-testid="footer-logo-home-link">
                            <SpideyLogo size={40} />
                        </Link>
                        <p className="mt-4 text-sm text-zinc-500 max-w-xs leading-relaxed">
                            Revente de Spider-Web noir & rouge.
                            Édition limitée, livraison offerte dès 50 €.
                        </p>
                        <div className="mt-6 flex gap-3">
                            <a
                                href="https://www.tiktok.com/@drip4saisons"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white hover:border-spidey-red hover:bg-spidey-red/10 transition-all"
                                aria-label="TikTok"
                                data-testid="social-tiktok-link"
                            >
                                <TikTokIcon />
                            </a>
                        </div>
                    </div>

                    <div>
                        <h4 className="font-mono-brand text-xs uppercase tracking-[0.25em] text-spidey-neon">
                            Rejoins le drop
                        </h4>
                        <p className="mt-4 text-sm text-zinc-500">
                            Sois prévenu des prochains coloris avant tout le monde.
                        </p>
                        <form onSubmit={subscribe} className="mt-4 flex gap-2">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="ton@email.fr"
                                className="flex-1 bg-black/60 border border-zinc-800 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-spidey-red"
                                data-testid="newsletter-email-input"
                            />
                            <button
                                type="submit"
                                className="w-11 h-11 shrink-0 rounded-full bg-spidey-red hover:bg-spidey-neon text-white flex items-center justify-center transition-all hover:scale-105"
                                aria-label="S'inscrire"
                                data-testid="newsletter-submit-button"
                            >
                                <Send size={15} />
                            </button>
                        </form>
                    </div>

                    <div>
                        <h4 className="font-mono-brand text-xs uppercase tracking-[0.25em] text-spidey-neon">
                            Infos
                        </h4>
                        <ul className="mt-4 space-y-2.5 text-sm text-zinc-500">
                            {INFO_LINKS.map((l) => (
                                <li key={l.to}>
                                    <Link to={l.to} className="hover:text-spidey-neon transition-colors">
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-6 flex flex-wrap gap-2">
                            {["VISA", "MASTERCARD", "APPLE PAY", "STRIPE"].map((b) => (
                                <span
                                    key={b}
                                    className="font-mono-brand text-[10px] tracking-wider text-zinc-500 border border-zinc-800 rounded px-2 py-1"
                                >
                                    {b}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-14 select-none pointer-events-none" aria-hidden="true">
                    <p className="font-display uppercase text-[clamp(3rem,10vw,8rem)] leading-none text-outline text-center">
                        Drip4saisons
                    </p>
                </div>

                <div className="mt-8 pt-6 border-t border-zinc-900 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-600 font-mono-brand">
                    <span>© 2026 DRIP4SAISONS — Tous droits réservés</span>
                    <span>Revente d'authentiques — non affilié à Marvel, Disney ou Crocs™</span>
                </div>
            </div>
        </footer>
    );
}
